import Fuse from 'fuse.js';

import type { ComputedCommandsSection, PaletteCommandDef } from '../types';

export const DEFAULT_COMMANDS_PRIORITY = 5;

export type MatchCommandsOptions = {
    /**
     * Threshold for the fuzzy search.
     */
    threshold?: number;
};

/**
 * Resolve the subtitle of a command.
 */
export function resolveSubtitle(command: PaletteCommandDef) {
    return typeof command.subtitle === 'function' ? command.subtitle() : command.subtitle;
}

/**
 * Filter a set of commands using a query.
 */
export function matchCommands(
    query: string,
    commands: PaletteCommandDef[],
    options: MatchCommandsOptions = {}
): Fuse.FuseResult<PaletteCommandDef>[] {
    if (!query) {
        return commands.map((c) => ({
            item: c,
            score: undefined,
        })) as Fuse.FuseResult<PaletteCommandDef>[];
    }

    const computedCommands = commands.map((command) => {
        const subtitle = resolveSubtitle(command);
        return {
            command,
            title: command.title,
            subtitle: subtitle || '',
            keywords: command.keywords || [],
        };
    });

    const fuse = new Fuse(computedCommands, {
        threshold: options.threshold ?? 0.3,
        location: 0,
        distance: 100,
        shouldSort: true,
        includeScore: true,
        minMatchCharLength: 1,
        isCaseSensitive: false,
        // Weight title higher than subtitle/keywords so direct title matches rank first
        keys: [
            { name: 'title', weight: 0.7 },
            { name: 'subtitle', weight: 0.15 },
            { name: 'keywords', weight: 0.15 },
        ],
    });
    return fuse.search(query).map((result) => {
        return {
            ...result,
            item: result.item.command,
        };
    });
}

/**
 * Filter and sort commands based on the search query.
 */
export function filterAndSortCommands(
    query: string,
    commands: PaletteCommandDef[],
    groupPriority?: number
) {
    const res = matchCommands(query, commands);

    // the priority is the highest matching score of the commands in the group
    let priority = groupPriority || DEFAULT_COMMANDS_PRIORITY;
    priority = res.reduce(
        (prev, x) => (typeof x.score !== 'undefined' ? Math.max((1 - x.score) * 10, prev) : prev),
        priority
    );
    commands = res.map<PaletteCommandDef>((r) => r.item);

    return { commands, priority };
}

/**
 * Sort and filter sections based on their priority and data length.
 */
export function sortAndFilterSections(section: ComputedCommandsSection[]) {
    return section
        .filter((section) => section.data.length > 0)
        .sort((a, b) => {
            if (a.priority > b.priority) {
                return -1;
            }
            if (a.priority < b.priority) {
                return 1;
            }
            return 0;
        });
}
